import React, { createContext, useContext, useEffect, useState } from 'react';
import type { SiteContent } from './types';

const ContentContext = createContext<SiteContent | null>(null);

/**
 * Restituisce i contenuti del sito caricati da /content.json.
 * Va usato solo all'interno di <ContentProvider>.
 */
export const useContent = (): SiteContent => {
  const content = useContext(ContentContext);
  if (!content) {
    throw new Error('useContent deve essere usato dentro <ContentProvider>');
  }
  return content;
};

const loaderStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: '#1A1414',
  color: 'rgba(255,255,255,0.55)',
  fontFamily: "'Manrope', system-ui, sans-serif",
  fontSize: 14,
  letterSpacing: '0.04em',
  zIndex: 9999,
};

export const ContentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [content, setContent] = useState<SiteContent | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    // `no-store` evita che il browser serva una versione cache del JSON:
    // così le modifiche fatte a content.json dopo il build appaiono subito.
    fetch(`${process.env.PUBLIC_URL}/content.json`, { cache: 'no-store' })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data: SiteContent) => setContent(data))
      .catch(() => setError(true));
  }, []);

  if (error) {
    return (
      <div style={loaderStyle}>
        Impossibile caricare i contenuti (content.json).
      </div>
    );
  }

  if (!content) {
    return <div style={loaderStyle} aria-busy="true">Caricamento…</div>;
  }

  return <ContentContext.Provider value={content}>{children}</ContentContext.Provider>;
};
